/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
// [APPLICATIONS PACKAGE TODO]: This module is scheduled for deprecation, as it should not
// reside in the root of the source directory. Please do not add new functions to this module,
// rather, please add them to `ion/applications/src/utils/helpers.ts` module.
import {ColumnState} from 'ag-grid-community';
import axios from 'axios';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import {isDesktop} from '@ion/core/src/capabilities/device';
import {findCurrentAccountMatch} from '@ion/core/src/entities/Accounts';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {entityId, getNoCacheHeaders} from '@ion/core/src/services/ConnectUtils';
import {
  AccountInfo,
  AccountLink,
  CTCEntityResponseWrapper,
  DeviceServiceTypes,
  Entity,
  GenericObject,
  ICNApplications,
  Link
} from '@ion/core/src/types/entities';
import {Operation} from '@ion/core/src/types/general';
import {Preference} from '@ion/core/src/types/preferences';
import {timeFormatter} from '@ion/core/src/utils/DateUtil';
import {PLATFORM_IDS} from '@ion/core/src/utils/DeviceUtils';
import {_mappedFilePremissions} from '@ion/dls/src/editors/features/ModuleActionHelpers';
import {getCurrentUser} from './pages/admin/users/useAuthenticatedUser';
import {
  configPlatformIds,
  DefaultSortParam,
  firmwarePlatformIds,
  vbuFirmwarePlatformIds
} from './pages/devices/ModularSearchUtils';

export interface ReInitializeStateParams {
  initialState: GenericObject;
  states?: string[];
};

export interface CreateSortParams {
  item: string;
  id: number | string;
  queryParams: GenericObject;
  columnKeys?: string[];
  isVbuFwEnabled?: boolean;
}

// defines the dashboard preference key
export const DASHBOARD_PREFERENCE = 'iOn-dashboard';

/**
 * Provides a utility for resolving an account id by using the provided id or
 * falling back to the currently persisted account id (user's default account).
 */
export const getAccountId = (getState: Function, accountId?: number): number => {
  const accId =  getState()?.globalGroup?.globalGroupId || entityId(getState()?.login?.user?.account?.href);
  return accountId || accId;
};

/**
 * Provides a utility for resolving an account id by using the provided id or
 * falling back to the currently selected global account id.
 */
export const getGroupId = (getState: Function, accountId?: number): number => {
  return accountId || getState().globalGroup.globalGroupId;
};
/**
 * Provides a utility for getting all subaccount ids for the given account id.
 */
export function getSubAccount(parentAccount, accountIds) {
  parentAccount.subAccounts?.forEach(sub => {
    const id = entityId(sub?.subaccount?.href || sub?.href);
    accountIds.push(id);
    const subAccounts = sub?.subaccount?.subAccounts || sub?.subAccounts;
    if (subAccounts) {
      getSubAccount(sub.subaccount || sub, accountIds);
    }
  });
  return accountIds;
};

/**
 * Returns all account ids for the authenticated user account hierarchy.
 */
export const getAccountIds = (getState: Function, accountId?: number): number[] => {
  const {accounts} = getState().initialization;
  const user       = getCurrentUser(getState);
  const id         = accountId || entityId(user?.account);
  const accountIds = accounts?.map(({href}) => entityId(href));
  accountIds?.unshift(id);
  return accountIds;
};

/**
 * Returns all account and subaccount ids for the authenticated user account hierarchy.
 */
export const getAllAccountIds = (getState, accountId?: number, includeParent: boolean = true): number[] => {
  const accountIds = [];
  const {globalGroup, globalGroupId, accountHierarchy} = getState().globalGroup;
  const [match] = findCurrentAccountMatch(accountHierarchy, globalGroup);
  if (match?.subAccounts) {
    const subAccounts = getSubAccount(match, []);
    accountIds.push(...subAccounts);
  }
  if (includeParent) {
    const id = accountId || globalGroupId;
    accountIds?.unshift(id);
  }
  return accountIds;
};

/**
 * Constructs an account hierarchy using the selected group as the top
 * level account
 */
export const getSelectedGroupHierarchy = (getState): AccountLink[] => {
  const {globalGroup, accountHierarchy} = getState().globalGroup;
  const match = findCurrentAccountMatch(accountHierarchy, globalGroup);
  return match;
};

/**
 * Provides an utility for setting the user's default account as parent account and its nested
 * accounts as subAccounts.
 */
export const setUserAccountHierarchy = (getState, accounts) => {
  const user = getCurrentUser(getState);
  const userAccount = {...user?.account, subAccounts: []};
  accounts?.forEach(account => {
    const subAccount = {
      subaccount : {...account}
    };
    userAccount.subAccounts.push(subAccount);
  });
  return [userAccount];
};

/**
 * Given a specific iOn Suite Grid columns path, if the user has chosen to
 * persist their Grid Column selections across sessions and devices, this
 * function will return the user persisted column selections and state for
 * the given grid (if any).
 *
 * ```typescript
 *
 *    import {getGridColumnState, setGridColumnState} from '../helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *    ...
 *
 *    [INITIALIZE_ADMIN_ASSETS] : (draft) => {
 *      draft.isInitialized = true;
 *      draft.grid.columnState = getGridColumnState(PREFERENCE);
 *    }
 *
 * ```
 *
 * @param   path The user preferences path for the given grid; e.g.
 *          'admin-assets.grid.desktop.columns', etc.
 * @param   forceFetch Allow to fetching the grid setting in user preference forcefully
 * @returns The persisted column state for the given grid.
 */
export const getGridColumnState = (page: string, forceFetch: boolean = false): ColumnState[] | undefined => {
  if (isPreferenceAvailable('persistGridLayouts') || forceFetch) {
    return UserPreferences.get(getPreferencePath(page)) as ColumnState[];
  }
};

/**
 * Given a specific iOn Suite Grid columns path, and the current state of the grid
 * columns for the given path, persists the column state to the path if the user has
 * chosen to persist their Grid Column selections, otherwise returns false.
 *
 * ```typescript
 *
 *    import {getGridColumnState, setGridColumnState} from '../helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *    ...
 *
 *    [SET_GRID_COLUMNS] : ({grid}: AdminAssetsState, {columnState}) => {
 *      grid.columnState = columnState;
 *      setGridColumnState(PREFERENCE, columnState);
 *    }
 *
 * ```
 *
 * @param   path The user preferences path for the given page; e.g.
 *          'admin-assets', etc.
 * @param   state The current state of the grid columns.
 * @param   autoSaveGrid Explicit check of auto save grid settings in user preference.
 * @returns The persisted column state for the given grid.
 */
export const setGridColumnState = debounce((
  page: string,
  state: ColumnState[],
  autoSaveGrid: boolean = false
): Promise<boolean> => {
  if (autoSaveGrid || isPreferenceAvailable('persistGridLayouts')) {
    return saveGridColumnState(page, state);
  }
  return Promise.resolve(false);
}, 3000);

/**
 * The utility for save grid column state in user preferences based on path.
 *
 *  ```typescript
 *
 *    import {saveGridColumnState} from '../helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *
 *    saveGridColumnState(PREFERENCE, columnState);
 *
 * ```
 *
 * @param page The user preferences path for the page
 * @param state The current state of the grid columns.
 * @returns The persisted column state for the given grid.
 */
export const saveGridColumnState = (page: string, state: ColumnState[]): Promise<boolean> => {
  return UserPreferences.set(getPreferencePath(page), state, !UserPreferences.hasChangeDetection());
};

/**
 * The utility function compares the previous and next state of the Grid column state.
 *
 *  ```typescript
 *
 *    import {hasChangeInColumnState} from '../helpers';
 *
 *    const hasChange = hasChangeInColumnState(preferedColumnState, nextColumnState);
 *
 * ```
 *
 * @param preState The previous grid column state
 * @param nxtState The next grid column state
 * @returns returns true if the state has change, else false
 */
export const hasChangeInColumnState = (preState: ColumnState[], nxtState: ColumnState[]): boolean => {
  return JSON.stringify(preState) !== JSON.stringify(nxtState);
};

/**
 * The utility for set grid column state in redux and user preferences based on path
 * if the user enabled the Automatic Grid saving option.
 *
 *  ```typescript
 *
 *    import {onSetGridColumns} from '../helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *
 *    onSetGridColumns({
 *      grid,
 *      isLoading  : assets.loading,
 *      preference : PREFERENCE
 *    }, columnState);
 *
 * ```
 *
 * @param grid grid redux state
 * @param isLoading indicates changes while loading
 * @param preference page preference.
 * @param columnState column state which holds the previous and current state
 */
export const onSetGridColumns = ({grid, isLoading, preference}, columnState) => {
  const {
    preferedColumnState,
    nextColumnState,
    autoSaveGrid
  }                = columnState;
  const preCheck   = !isLoading && !autoSaveGrid;
  grid.hasUpdate   = preCheck && hasChangeInColumnState(preferedColumnState, nextColumnState);
  grid.columnState = nextColumnState;
  setGridColumnState(preference, nextColumnState, autoSaveGrid);
  autoSaveGrid && (grid.preferedColumnState = nextColumnState);
};

/**
 * The utility for set grid column state in redux and user preferences based on path.
 *
 *  ```typescript
 *
 *    import {onUpdateGridColumns} from '../helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *
 *    onUpdateGridColumns({
 *      grid,
 *      preference : PREFERENCE
 *    }, columnState);
 *
 * ```
 *
 * @param grid grid redux state
 * @param preference page preference.
 * @param columnState column state which holds the previous and current state
 */
export const onUpdateGridColumns = ({grid, preference}, columnState) => {
  const {nextColumnState}   = columnState;
  grid.hasUpdate            = false;
  grid.preferedColumnState  = nextColumnState;
  saveGridColumnState(preference, nextColumnState);
};

/**
 * Provides a utility which allows for determining if the UserPreferences API has been
 * initialized and if the given preference is defined.
 */
export const isPreferenceAvailable = (preference?: string) => {
  return UserPreferences.isInitialized() && (preference ? UserPreferences.get(preference) : true);
};

/**
 * @private
 *
 * Provides a convenience function which determines and returns the standardized grid
 * columns preferences path.
 */
const getPreferencePath = (page: string): string => {
  return `${page}:grid.columns.${isDesktop() ? 'desktop' : 'mobile'}`;
};

/**
 * Returns all account and subaccount ids for the authenticated user account hierarchy.
 */
export const getAllAccounts = (getState, accountId?: number, includeParent: boolean = true): Link[] => {
  const accountLinks = [];
  const {accounts} = getState().initialization;
  accounts?.forEach(account => {
    const id = entityId(account?.href);
    accountLinks.push(account);
    if (account?.subAccounts) {
      getSubAccounts(account, accountLinks);
    }
  });
  if (includeParent) {
    const user  = getCurrentUser(getState);
    const account = accountId || user?.account;
    accountLinks?.unshift(account);
  }
  return accountLinks;
};

/**
 * Provides a utility for getting all subaccount ids for the given account id.
 */
export function getSubAccounts(parentAccount, accountIds) {
  parentAccount.subAccounts.forEach(sub => {
    accountIds.push(sub?.subaccount);
    if (sub?.subaccount?.subAccounts) {
      getSubAccounts(sub.subaccount, accountIds);
    }
  });
  return accountIds;
};

/**
 * The function that will handles the persistence of dashboard saved widget list. The same function will
 * act as both getter and setter function based on the clientToServer flag.
 *
 * ```typescript
 *
 *  import {dashboardPersistence} from './helpers';
 *
 *  dashboardPersistence() // getter function
 *  dashboardPersistence(state, true) // setter function
 *
 * ```
 *
 * @param state dashboard state in redux
 * @param clientToServer indicates the flow of call.
 */
export const dashboardPersistence = (state: GenericObject = {}, clientToServer: boolean = false) => {
  if (clientToServer) {
    return UserPreferences.set(DASHBOARD_PREFERENCE, state, !UserPreferences.hasChangeDetection());
  } else {
    return UserPreferences.get(DASHBOARD_PREFERENCE);
  }
};

export const validateAccounts = <EntityType>(
  accountLinks: Link[],
  entities: Array<CTCEntityResponseWrapper<EntityType>>,
  type: Entity
) => {
  const resolved: Array<CTCEntityResponseWrapper<EntityType>>   = [];
  const unresolved: Array<CTCEntityResponseWrapper<EntityType>>   = [];
  entities.forEach(entity => {
    const item  = type ? entity[type] : entity;
    const title = (item as any)?.groupName || (item as any)?.parentGroup;
    const validAccount = accountLinks?.filter((accountLink) => accountLink.title === title)[0];
    if (validAccount) {
      const rel   = (item as any)?.groupName ? 'account' : 'parent';
      const tempAccount = {href: validAccount.href, rel: 'account', status: validAccount.status, title: validAccount.title};
      item[rel] = tempAccount;
      delete (item as any).groupName;
      delete (item as any).parentGroup;
      resolved.push(entity);
    } else {
      unresolved.push(entity);
    }
  });
  return {resolved, unresolved};
};

/**
 * Provides a utility which returns current User's notifications displaying prefrences
 */
export const getUserNotificationsPreferences = () => {
  if (UserPreferences.isInitialized()) {
    return UserPreferences.get([
      Preference.showNotifications,
      Preference.showUrgentNotifications,
      Preference.showGeneralNotifications,
      Preference.showTimeRelatedNotifications,
      Preference.showConnectivityNotifications,
      Preference.showScheduledEventsNotifications
    ]);
  }
};

/**
 * Provides a utility which returns current User's notifications displaying prefrences
 */
export const getDeviceServices = (userApplications: GenericObject[]) => {
  const servicesArr = {icn: false, ble: false, iOn: false};
  const icnApps = Object.values(ICNApplications);
  userApplications?.map((application) => {
    const title = application?.title;
    let appKey = application?.href;
    appKey = appKey.substring(appKey.lastIndexOf('/') + 1);
    if (icnApps.indexOf(appKey) > -1) {
      servicesArr.icn = true;
    }
    if (title === DeviceServiceTypes.tagDetection) {
      servicesArr.ble = true;
    }
    if (title === DeviceServiceTypes.videoService) {
      servicesArr.iOn = true;
    }
  });
  return servicesArr;
};

/**
 * Validate devices data uploaded from excel
 */
export const validateDevices = (devices: Array<CTCEntityResponseWrapper<any>>) => {
  const valid   = [];
  const invalid = [];

  devices.forEach(({device}) => {
    const {
      name,
      type,
      deviceConfiguration,
      serialNumber,
      esn,
      meid,
      iccid,
      imei,
      imei2,
      imsi,
      mdn,
      externalId,
      ipAddress
    } = device || {};

    let isInvalid = (!name || !type || !deviceConfiguration);

    if (!isInvalid) {
      isInvalid = (
        !serialNumber && !esn && !meid && !iccid && !imei && !imei2 && !imsi && !mdn && !externalId && !ipAddress
      );
    } if (isInvalid) {
      invalid.push({device});
    } else {
      valid.push({device});
    }
  });
  return {valid, invalid};
};

/**
 * Validate manufactured devices data uploaded from excel
 */
export const validateManufacturedDevices = (manufacturedDevices: Array<CTCEntityResponseWrapper<any>>) => {
  const valid       = [];
  const invalid   = [];
  manufacturedDevices.forEach((manufacturedDevice) => {
    if (manufacturedDevice) {
      if (!manufacturedDevice.device.type) {
        invalid.push({manufacturedDevice});
      } else {
        valid.push(manufacturedDevice);
      }
    }
  });
  return {valid, invalid};
};

/**
 * Validate assets data uploaded from excel
 */
export const validateAssets = (assets: Array<CTCEntityResponseWrapper<any>>) => {
  const valid     = [];
  const invalid   = [];
  assets.forEach(({asset}) => {
    if (!asset.name) {
      invalid.push({asset});
    } else {
      valid.push({asset});
    }
  });
  return {valid, invalid};
};

/**
 * The Utility for downloading device files and release notes from the grid.
 */
export const downloadBlob = (url, name, onNotFound) => {
  const header = getNoCacheHeaders(false);
  axios({
    url             : url,
    method          : 'GET',
    responseType    : 'blob',
    headers         : header?.headers,
    withCredentials : false
  }).then((response) => {
    const url     = window.URL.createObjectURL(new Blob([response.data]));
    const link    = document.createElement('a');
    const ext     = name.slice((name.lastIndexOf('.') - 1 >>> 0) + 2);
    link.href     = url;
    link.download = name;
    !ext ? link.setAttribute('download', `"${name}.${''}"`) : link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
  }).catch(function(err) {
    if (isEqual(err?.response?.status, 404)) {
      onNotFound?.();
    } else {
      return error(err);
    }
  });
};

/**
 * The createRoleSearchTerms utility iterates the list of imported Users and
 * returns an array of search terms for each group which contains Role Name.
 */
export const createRoleSearchTerms = (resolved, accountId) => {
  const groupRoles = {};
  const roles = [];
  resolved.forEach(({user}) => {
    const {roles} = user;
    if (roles?.length) {
      groupRoles[accountId] = groupRoles[accountId] || [];
      groupRoles[accountId].push(entityId(roles[0].href));
    }
  });
  Object.keys(groupRoles).forEach(groupId => {
    roles.push({
      name : {
        value    : groupRoles[groupId].toString(),
        wildcard : false
      },
      account : {
        value : groupId
      }
    });
  });
  return roles;
};

export const getSortArray = (platforms: number[], sortOrder, isVbuFwEnabled = false) => (
  [
    {
      'extensions.fileVersions.version' : {
        order   : sortOrder[0] ? 'desc' : 'asc',
        missing : isVbuFwEnabled ? '_last' : '_missing',
        ...({
          nested : {
            path   : 'extensions.fileVersions',
            filter : {
              terms : {
                'extensions.fileVersions.platform' : platforms
              }
            }
          }
        })
      }
    }
  ]
);

const getMDNSortArray = (sortOrder) => ([{
  min : {
    order : sortOrder[0] ? 'desc' : 'asc'
  }
}]);

// determines jpod specific sorting ...
export const getJPODSortOn = (sort, sortOrder, isVbuFwEnabled = true) => {
  const type = sort?.replace('-', '');
  if (type === 'JPOD Firmware' && isVbuFwEnabled) {
    return getSortArray(firmwarePlatformIds, sortOrder);
  } else if (type === 'JPOD Firmware' && !isVbuFwEnabled) {
    return getSortArray(vbuFirmwarePlatformIds, sortOrder, true);
  } else if (type === 'JPOD Config') {
    return getSortArray(configPlatformIds, sortOrder);
  } else if (type === 'BLE3 Firmware') {
    return getSortArray([PLATFORM_IDS.ble3Firmware], sortOrder);
  } else if (type === 'VBU3 Firmware') {
    return getSortArray([PLATFORM_IDS.vbu3Firmware], sortOrder);
  } else if (type === 'BSP Modem Firmware') {
    return getSortArray([PLATFORM_IDS.bspModemFirmware], sortOrder);
  } else if (type === 'Cell Radio Firmware') {
    return getSortArray([PLATFORM_IDS.cellRadioFirmware], sortOrder);
  } else if (type === 'mdn') {
    return getMDNSortArray(sortOrder);
  }
};

// determines sorting based on type
export const getSortOn = (sort, sortOrder, isVbuFwEnabled = true) => {
  const sortOn = getJPODSortOn(sort, sortOrder, isVbuFwEnabled);
  return sortOn || [];
};

/**
 * Helper function to return the JSON formatted search terms needed for the given search type
 */
export const createSortParams = ({
  item,
  id,
  queryParams,
  columnKeys,
  isVbuFwEnabled = true
}: CreateSortParams) => {
  const sortOrder        = [item?.includes('-')];
  const must_not         = queryParams.filter(item => item.not_match).map(e => e.not_match);
  const nestedSearch     = queryParams.filter(item => item.nestedSearch).map(e => e.nestedSearch);
  const sortParam        = item?.replace('-', '');
  const sort             = getSortOn(item, sortOrder, isVbuFwEnabled);
  const defaultSortParam = getDefaultSortParam(sortParam);
  const hasExtensions    = hasDefaultExtensionsSortParam(sortParam);

  const defaultSort = {
    [defaultSortParam] : {
      order : sortOrder[0] ? 'desc' : 'asc',
      ...(hasExtensions && {missing: '_missing'})
    }
  };

  sort.push(defaultSort);

  const query = {
    query : nestedSearch[0],
    sort
  };
  if (!query?.query) {
    const queryStringValue = queryParams.find(item => item.query_string)?.query_string?.query;
    const should           = columnKeys?.filter(key => !!key)?.map(key => ({wildcard: {[key]: queryStringValue}}));
    let must = queryParams.filter(item => ((
      !columnKeys || !item.query_string ||
      item.query_string?.fields?.length === 1) &&
      (!item.not_match || !item.nestedSearch)
    ));
    if (queryStringValue && !!columnKeys?.length) {
      must = [
        ...must,
        {bool: {should}}
      ];
    }
    query.query = {
      bool : {
        must,
        must_not
      }
    };
  }
  return {
    query,
    accountId : id
  };
};

export const getJobNameTimestamp = () => timeFormatter(new Date(), 'YYYYMMDD_HHmmss', true);

/**
 * Helper function to return action based on Job/Device File creation.
 */
export const getAction = (
  isDeviceFile = false,
  isDeviceFileEditor = false
) => {
  if (isDeviceFile || isDeviceFileEditor) {
    return Operation.Create;
  }
  return Operation.Assign;
};

/**
 * Helper function to return permissions for the available file permission features.
 */
export const getFilePermissionsPermission = (filePermissions) => {
  const filteredFilePermissions = [];
  filePermissions?.forEach((item) => {
    const filePermissions = _mappedFilePremissions?.[item];
    filteredFilePermissions.push(...filePermissions);
  });
  return filteredFilePermissions;
};

/**
 * Reinitializes state properties to their initial values based on a provided initialState object.
 *
 * @param draft - The mutable state object, typically managed by Immer or a similar library.
 * @param initialState - The object containing the initial state values.
 * @param states - An array of property keys that should not be reinitialized.
 */
export const reInitializeState = (draft: GenericObject, {
  initialState,
  states = []
}: ReInitializeStateParams) => {
  const restore = new Set(states);
  // Iterate over the properties of the initialState object.
  for (const key in initialState) {
    if (!restore.has(key)) {
      draft[key] = initialState[key];
    }
  }
  return draft;
};

/**
 * @processErrorMessage
 * Processes the error message and appends the application titles to the error message.
 *
 * @param err - Error object
 * @param accountInfo - Account information object to parse from the error message
 * @returns - Processed error message otherwise the original error message
 */
export const processErrorMessage = function(err: any, accountInfo): string {
  // Regular expression to match the array-like substring
  const arrayRegex     = /\[(.*?)\]/;
  const {errorText}    = err || {};
  const cleanErrorText = errorText?.replace?.(/\s*\[.*?\]\s*/, ' ')?.trim();
  const uuidMatch      = errorText?.match?.(arrayRegex);
  const {
    error: {message = {}} = {}
  } = errorText || {};
  const errorMessage   = !isEmpty(message) ? message : errorText;
  
  // If no UUIDs are found in the error text, return the original error message.
  if (!uuidMatch) {
    return errorMessage;
  }

  // Extract UUIDs and find matching applications.
  const uuids                = uuidMatch[1].split(',').map(s => s.trim());
  const matchingApplications = findApplicationsByUuid(accountInfo, uuids);

  // If there are matching applications, append their titles to the error message.
  if (matchingApplications.length > 0) {
    const applicationTitles = matchingApplications.map(app => app.title).join(', ');
    return `${cleanErrorText} ${applicationTitles}`;
  } else {
    // If there are no matching UUIDs, return the original error text.
    return errorMessage;
  }
};

/**
 * Provides a utility for finding applications by UUID from an account object an error message.
 */
export const findApplicationsByUuid = (accountInfo: AccountInfo, uuids: string[]) => {
  if (!accountInfo?.account?.applications) {
    error('Applications array is undefined.');
    return [];
  }
  return accountInfo.account.applications.filter(app => {
    const appUuid = app.href.split('/').pop();
    return uuids.includes(appUuid);
  });
};

/**
 * Listing the missing additional temporary fields that need to be added until CTC team
 * indexed for the _comboField.
 */
const nonIndexedComboFields = [
  'account.title',
  'asset.title',
  'architecture',
  'category',
  'deviceConfiguration.title',
  'extensions.fileVersions.configVersion',
  'extensions.fileVersions.firmwareVersion',
  'status',
  'type.title',
  'pegBehavior.title',
  'imei2'
];

/**
 * Add the searchable properites which is not added from
 * CTC end to support any search in devices smart search.
 */
export const getComboFields = () => [
  '_comboField', ...nonIndexedComboFields
];

/**
 * Helper function to return default sort or sortParam based on user sort.
 */
export const getDefaultSortParam = (sortParam: string): string => {
  switch (sortParam) {
    case DefaultSortParam.JPOD_FIRMWARE:
    case DefaultSortParam.JPOD_CONFIG:
    case DefaultSortParam.BLE3_FIRWARE:
    case DefaultSortParam.VBU3_FIRWARE:
    case DefaultSortParam.BSP_MODEM_FIRWARE:
    case DefaultSortParam.CELL_RADIO_FIRWARE:
      return DefaultSortParam.ID;
    default:
      return sortParam;
  }
};

/**
 * Define Default Subscriptions for the Message End Point.
 */
export const deafultMsgEPSubscriptions = ['Connect Basic Push', 'Connect Pro Push'];

/**
 * Check the default Message End Point subscriptions and 
 * verify with saved one and return the boolean value based on 
 * the matches.
 */
export const isDeleteSubscriptionAvilable = (savedSubscription: String): boolean => deafultMsgEPSubscriptions.some(
  val => val === savedSubscription) || false;

/**
 * Check the enterprise toggle and check with saved subscription and return the boolean
 * value based on the conditions.
 */
export const isDeleteSubMEP = (isEnterprise: boolean, savedSubscription: String, subscription: String): boolean => {
  if (isEnterprise && (savedSubscription !== subscription)) {
    return isDeleteSubscriptionAvilable(savedSubscription);
  }
  return false;
};
/*
 * Provides a utility to return boolean value based on user sort..
 */
export const hasDefaultExtensionsSortParam = (sortParam: string): boolean => {
  switch (sortParam) {
    case DefaultSortParam.APP_ID:
    case DefaultSortParam.FIRMWARE_VERSION:
    case DefaultSortParam.CONFIGURATION_VERSION:
      return true;
    default:
      return false;
  }
};

/**
 * Helper function to return boolean value based on user sort.
 */
export const hasExtensions = (sortParam: string[]): boolean => {
  switch (sortParam?.[0]) {
    case DefaultSortParam.JPOD_FIRMWARE:
    case DefaultSortParam.JPOD_CONFIG:
    case DefaultSortParam.BLE3_FIRWARE:
    case DefaultSortParam.VBU3_FIRWARE:
    case DefaultSortParam.BSP_MODEM_FIRWARE:
    case DefaultSortParam.CELL_RADIO_FIRWARE:
    case DefaultSortParam.FIRMWARE_JPOD:
    case DefaultSortParam.CONFIG_JPOD:
    case DefaultSortParam.FIRWARE_BLE3:
    case DefaultSortParam.FIRWARE_VBU3:
    case DefaultSortParam.FIRWARE_BSP_MODEM:
    case DefaultSortParam.FIRWARE_CELL_RADIO:
      return true;
    default:
      return false;
  }
};

/**
 * Helper function to return extension key based on user sort.
 */
export const hasExtensionsKey = item => DefaultSortParam.FILE_VERSION in item;

/**
 * Helper function to check the given domains are valid based on the condition 
 * passed in the second argument.
 */
export const containsInvalidDomains =  (domains: string | Array, resolver: (item: string) => boolean): boolean => {
  const hasInvalidDomains = domains?.filter(resolver)?.length > 0;
  return !hasInvalidDomains;
};
